<template>
  <span>
    <v-row
      dense
      v-for="card in cardMunicipiosChartsComputed"
      :key="card.highchartsOptionsAndRef"
    >
      <v-col cols="12">
        <v-card-charts-dashboard
          :title="card.title"
          :chart-ref="card.ref"
          :options="card.options"
          :deepCopyOnUpdate="card.deepCopyOnUpdate"
          :isLoading="card.loading"
        />
      </v-col>
    </v-row>
  </span>
</template>

<script>
import VCardChartsDashboard from "@/components/base/VCardChartsDashboard.vue";

export default {
  components: {
    VCardChartsDashboard,
  },
  name: "DashboardLnoVisaoMunicipiosCardsKpisPorMunicipio",
  props: {
    dados: {
      type: Array,
      required: true,
    },
    municipios: {
      type: Array,
      required: true,
    },
    metasKpis: {
      type: Object,
      required: true,
    },
    tipoOrdenacao: {
      type: String,
      required: true,
    },
    kpiOrdenacao: {
      type: String,
      required: true,
    },
    quantidadeMunicipios: {
      type: Number,
      required: true,
    },
    exportingOptions: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    chartQlpOptions: {},
    chartPlaOptions: {},
    chartPmea15Options: {},
    chartPmea30Options: {},
    chartPmea45Options: {},
    chartPat1Options: {},
    chartPat2Options: {},
    chartPat3Options: {},
    chartPat4Options: {},
    cachedMunicipiosFiltrados: null,
    lastDadosHash: null,
    lastFiltrosHash: null,
    debounceTimer: null,
    chartsLoaded: false,
    cardMunicipiosCharts: [
      {
        title: "QLP - Qtde. de Ligações com obra pendentes não suspensas",
        highchartsOptionsAndRef: "chartQlpOptions",
        deepCopyOnUpdate: false,
        loadingKey: "loading",
      },
      {
        title: "PLA - Percentual de ligações com obra atrasadas",
        highchartsOptionsAndRef: "chartPlaOptions",
        deepCopyOnUpdate: false,
        loadingKey: "loading",
      },
      {
        title: "PMEA15 - Prazo Médio de Estudos Atrasados (15 dias)",
        highchartsOptionsAndRef: "chartPmea15Options",
        deepCopyOnUpdate: false,
        loadingKey: "loading",
      },
      {
        title: "PMEA30 - Prazo Médio de Estudos Atrasados (30 dias)",
        highchartsOptionsAndRef: "chartPmea30Options",
        deepCopyOnUpdate: false,
        loadingKey: "loading",
      },
      {
        title: "PMEA45 - Prazo Médio de Estudos Atrasados (45 dias)",
        highchartsOptionsAndRef: "chartPmea45Options",
        deepCopyOnUpdate: false,
        loadingKey: "loading",
      },
      {
        title: "PAT1 - Prazo Médio de Lig. Atrasada do Tipo 1",
        highchartsOptionsAndRef: "chartPat1Options",
        deepCopyOnUpdate: false,
        loadingKey: "loading",
      },
      {
        title: "PAT2 - Prazo Médio de Lig. Atrasada do Tipo 2",
        highchartsOptionsAndRef: "chartPat2Options",
        deepCopyOnUpdate: false,
        loadingKey: "loading",
      },
      {
        title: "PAT3 - Prazo Médio de Lig. Atrasada do Tipo 3",
        highchartsOptionsAndRef: "chartPat3Options",
        deepCopyOnUpdate: false,
        loadingKey: "loading",
      },
      {
        title: "PAT4 - Prazo Médio de Lig. Atrasada do Tipo 4",
        highchartsOptionsAndRef: "chartPat4Options",
        deepCopyOnUpdate: false,
        loadingKey: "loading",
      },
    ],
  }),
  computed: {
    cardMunicipiosChartsComputed() {
      return this.cardMunicipiosCharts.map((card) => {
        return {
          ...card,
          options: this[card.highchartsOptionsAndRef],
          ref: card.highchartsOptionsAndRef,
          loading: !!this.loading,
        };
      });
    },
    municipiosFiltrados() {
      const dadosHash = JSON.stringify(this.dados);
      const filtrosHash = JSON.stringify({
        tipoOrdenacao: this.tipoOrdenacao,
        kpiOrdenacao: this.kpiOrdenacao,
        quantidadeMunicipios: this.quantidadeMunicipios,
      });

      if (
        this.cachedMunicipiosFiltrados &&
        this.lastDadosHash === dadosHash &&
        this.lastFiltrosHash === filtrosHash
      ) {
        return this.cachedMunicipiosFiltrados;
      }

      const municipiosOrdenados = this.getMunicipiosOrdenados();
      const resultado =
        this.quantidadeMunicipios === 0
          ? municipiosOrdenados
          : municipiosOrdenados.slice(0, this.quantidadeMunicipios);

      this.cachedMunicipiosFiltrados = resultado;
      this.lastDadosHash = dadosHash;
      this.lastFiltrosHash = filtrosHash;

      return resultado;
    },
  },
  mounted() {
    this.setChartsOptions();
  },
  methods: {
    debounce(func, delay) {
      return (...args) => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => func.apply(this, args), delay);
      };
    },
    getMunicipiosOrdenados() {
      const dadosCopy = [...this.dados];
      const sortedValues =
        this.tipoOrdenacao === "asc"
          ? dadosCopy.sort(
              (a, b) =>
                Number(a[this.kpiOrdenacao] ?? 0) -
                Number(b[this.kpiOrdenacao] ?? 0),
            )
          : dadosCopy.sort(
              (a, b) =>
                Number(b[this.kpiOrdenacao] ?? 0) -
                Number(a[this.kpiOrdenacao] ?? 0),
            );

      return sortedValues.map((el) => el.nome_municipio);
    },
    setChartsOptions() {
      this.debounce(() => {
        this.setChartQlpOptions();
        this.setChartPlaOptions();
        this.setChartPmea15Options();
        this.setChartPmea30Options();
        this.setChartPmea45Options();
        this.setChartPat1Options();
        this.setChartPat2Options();
        this.setChartPat3Options();
        this.setChartPat4Options();
        this.chartsLoaded = true;
      }, 100)();
    },
    createChartData(kpiName, kpiKey, yAxisMax = null) {
      const options = this.getColumnChartDefaultOptions();
      const municipiosFiltrados = this.municipiosFiltrados;
      const dadosMap = new Map(this.dados.map((d) => [d.nome_municipio, d]));

      if (yAxisMax !== null) {
        options.yAxis.max = yAxisMax;
      }

      options.series = [
        {
          name: kpiName,
          animation: false,
          data: municipiosFiltrados.map((nomeMunicipio, index) => {
            const dados = dadosMap.get(nomeMunicipio);
            return dados
              ? {
                  x: index,
                  y: Number(dados[kpiKey] || 0),
                  color: this.getCorBarra(
                    kpiKey,
                    dados[kpiKey],
                    dados.codigo_ibge,
                  ),
                }
              : { x: index, y: null };
          }),
        },
      ];

      return options;
    },
    getColumnChartDefaultOptions() {
      return {
        chart: {
          type: "column",
          height: 400,
          zooming: {
            type: "x",
          },
          animation: false,
        },
        credits: { enabled: false },
        title: { text: null },
        subtitle: { text: null },
        legend: { enabled: false },
        xAxis: {
          categories: this.municipiosFiltrados,
          crosshair: true,
          title: {
            text: "Município",
          },
        },
        yAxis: {
          title: { text: null },
        },
        lang: {
          noData: "Sem dados para exibição",
        },
        series: [],
        exporting: this.exportingOptions,
      };
    },
    getCorBarra(indicador, valor, codigoIbge) {
      const meta = this.metasKpis?.[codigoIbge];
      if (!meta) {
        return this.$vuetify.theme.themes.light.primary;
      }

      const valorMeta = indicador.startsWith("pmea")
        ? meta.pmea || 0
        : meta[indicador] || 0;

      return Number(valor) > Number(valorMeta)
        ? this.$vuetify.theme.themes.light.primary
        : this.$vuetify.theme.themes.light.secondary;
    },
    setChartQlpOptions() {
      this.chartQlpOptions = this.createChartData("QLP", "qlp");
    },
    setChartPlaOptions() {
      this.chartPlaOptions = this.createChartData("PLA", "pla", 100);
    },
    setChartPmea15Options() {
      this.chartPmea15Options = this.createChartData("PMEA15", "pmea15");
    },
    setChartPmea30Options() {
      this.chartPmea30Options = this.createChartData("PMEA30", "pmea30");
    },
    setChartPmea45Options() {
      this.chartPmea45Options = this.createChartData("PMEA45", "pmea45");
    },
    setChartPat1Options() {
      this.chartPat1Options = this.createChartData("PAT1", "pat1");
    },
    setChartPat2Options() {
      this.chartPat2Options = this.createChartData("PAT2", "pat2");
    },
    setChartPat3Options() {
      this.chartPat3Options = this.createChartData("PAT3", "pat3");
    },
    setChartPat4Options() {
      this.chartPat4Options = this.createChartData("PAT4", "pat4");
    },
  },
  watch: {
    dados: {
      handler() {
        this.cachedMunicipiosFiltrados = null;
        this.lastDadosHash = null;
        this.lastFiltrosHash = null;
        this.setChartsOptions();
      },
      deep: true,
    },
    municipiosFiltrados() {
      this.setChartsOptions();
    },
    tipoOrdenacao() {
      this.cachedMunicipiosFiltrados = null;
      this.lastFiltrosHash = null;
      this.setChartsOptions();
    },
    kpiOrdenacao() {
      this.cachedMunicipiosFiltrados = null;
      this.lastFiltrosHash = null;
      this.setChartsOptions();
    },
    quantidadeMunicipios() {
      this.cachedMunicipiosFiltrados = null;
      this.lastFiltrosHash = null;
      this.setChartsOptions();
    },
  },
};
</script>

<style></style>
